import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Grid, TextField, Button, Typography,
  Autocomplete, MenuItem, Select, FormControl, InputLabel,
  Chip, IconButton, Divider, CircularProgress, Alert, LinearProgress
} from '@mui/material';
import { ArrowBack, Save, Payment, Description } from '@mui/icons-material';
import { API } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import LineItemsEditor from '../components/LineItemsEditor';
import PaymentDialog from '../components/payments/PaymentDialog';
import { STATUS_COLORS, STATUS_LABELS, fmt } from '../utils';
import { useSnackbar } from 'notistack';

const DEFAULT_FORM = {
  client: null, seller: '', items: [{ description: '', qty: 1, unitPrice: 0, discount: 0, taxRate: 16 }],
  paymentTerms: 'COD', currency: 'MXN',
  publicNote: 'Precios en moneda MXN',
  footerNote: 'Gracias por su preferencia',
  privateNote: '', status: 'borrador'
};

export default function InvoiceFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const isNew = !id;

  const [form, setForm] = useState(DEFAULT_FORM);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [payDialog, setPayDialog] = useState(false);

  useEffect(() => {
    API.get('/auth/users').then(r => setUsers(r.data));
    if (!isNew) {
      setLoading(true);
      API.get(`/invoices/${id}`).then(r => {
        setInvoice(r.data);
        setForm({ ...DEFAULT_FORM, ...r.data, client: r.data.client });
      }).finally(() => setLoading(false));
    }
  }, [id, isNew]);

  useEffect(() => {
    API.get('/clients', { params: { search: clientSearch, limit: 10 } }).then(r => setClients(r.data.clients));
  }, [clientSearch]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSave = async () => {
    if (!form.client) return enqueueSnackbar('Selecciona un cliente', { variant: 'error' });
    setSaving(true);
    try {
      const payload = { ...form, client: form.client?._id || form.client, seller: form.seller || user?.id };
      if (isNew) await API.post('/invoices', payload);
      else await API.put(`/invoices/${id}`, payload);
      enqueueSnackbar(isNew ? 'Factura creada' : 'Factura guardada', { variant: 'success' });
      navigate('/facturas');
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const totals = form.items.reduce((acc, item) => {
    const base = (item.qty || 0) * (item.unitPrice || 0);
    const disc = base * ((item.discount || 0) / 100);
    const taxable = base - disc;
    const tax = taxable * ((item.taxRate || 0) / 100);
    return { sub: acc.sub + taxable, tax: acc.tax + tax };
  }, { sub: 0, tax: 0 });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;

  const isReadonly = ['pagada', 'cancelada'].includes(invoice?.status);
  const paidPct = invoice?.total > 0 ? ((invoice?.paid || 0) / invoice.total) * 100 : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/facturas')}><ArrowBack /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={800}>{isNew ? 'Nueva factura' : `Factura ${invoice?.number}`}</Typography>
          {invoice && <Chip label={STATUS_LABELS[invoice.status]} color={STATUS_COLORS[invoice.status]} size="small" sx={{ mt: 0.5 }} />}
        </Box>
        {!isReadonly && <Button variant="outlined" startIcon={<Save />} onClick={handleSave} disabled={saving}>Guardar</Button>}
        {!isNew && !['pagada','cancelada'].includes(invoice?.status) && (
          <Button variant="contained" color="success" startIcon={<Payment />} onClick={() => setPayDialog(true)}>
            Aplicar pago
          </Button>
        )}
      </Box>

      {isReadonly && <Alert severity="info" sx={{ mb: 2 }}>Esta factura está {STATUS_LABELS[invoice?.status]?.toLowerCase()}.</Alert>}

      {invoice && invoice.paid > 0 && (
        <Card sx={{ mb: 2, background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)' }}>
          <CardContent sx={{ py: '12px !important', px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>Progreso de pago</Typography>
              <Typography variant="body2" fontWeight={700} color="success.main">{fmt.currency(invoice.paid)} cobrado de {fmt.currency(invoice.total)}</Typography>
            </Box>
            <LinearProgress variant="determinate" value={paidPct} color={paidPct >= 100 ? 'success' : 'warning'}
              sx={{ height: 8, borderRadius: 4 }} />
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={clients} getOptionLabel={o => o.name || ''}
                    value={form.client} onChange={(_, val) => set('client', val)}
                    onInputChange={(_, val) => setClientSearch(val)} disabled={isReadonly}
                    renderOption={(props, o) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{o.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{o.rfc || o.email}</Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={params => <TextField {...params} label="Cliente *" size="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Vendedor</InputLabel>
                    <Select value={form.seller || user?.id || ''} onChange={e => set('seller', e.target.value)} label="Vendedor" disabled={isReadonly}>
                      {users.map(u => <MenuItem key={u._id} value={u._id}>{u.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Cond. de pago" value={form.paymentTerms}
                    onChange={e => set('paymentTerms', e.target.value)} disabled={isReadonly} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>Artículos</Typography>
              <LineItemsEditor items={form.items} onChange={v => set('items', v)} disabled={isReadonly} />
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nota pública" value={form.publicNote}
                    onChange={e => set('publicNote', e.target.value)} multiline rows={3} disabled={isReadonly} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nota al pie" value={form.footerNote}
                    onChange={e => set('footerNote', e.target.value)} multiline rows={3} disabled={isReadonly} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>Resumen</Typography>
              {form.client && (
                <Box sx={{ mb: 2, p: 1.5, background: 'rgba(107,33,168,0.05)', borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">Facturar a:</Typography>
                  <Typography variant="body2" fontWeight={600}>{form.client.name}</Typography>
                  <Typography variant="caption">{form.client.rfc}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                <Typography variant="body2" fontWeight={600}>{fmt.currency(totals.sub)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">IVA:</Typography>
                <Typography variant="body2" fontWeight={600}>{fmt.currency(totals.tax)}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Typography variant="h6" fontWeight={800}>Total:</Typography>
                <Typography variant="h6" fontWeight={800} color="primary.main">{fmt.currency(totals.sub + totals.tax)}</Typography>
              </Box>

              {!isReadonly && (
                <Button fullWidth variant="contained" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <Save />} sx={{ mt: 3 }}>
                  {isNew ? 'Crear factura' : 'Guardar cambios'}
                </Button>
              )}

              {/* Linked quotes */}
              {invoice?.quotes?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing="0.1em" color="primary.main">
                    Cotizaciones origen
                  </Typography>
                  {invoice.quotes.map(q => (
                    <Box key={q._id} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, cursor: 'pointer', p: 1, borderRadius: 1, '&:hover': { background: '#F5F3FF' } }}
                      onClick={() => navigate(`/cotizaciones/${q._id}/editar`)}>
                      <Typography variant="body2" fontWeight={600} color="primary.main">{q.number}</Typography>
                      <Chip label={STATUS_LABELS[q.status]} color={STATUS_COLORS[q.status]} size="small" />
                    </Box>
                  ))}
                </Box>
              )}

              {/* Payment history */}
              {invoice?.payments?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing="0.1em" color="success.main">
                    Pagos aplicados
                  </Typography>
                  {invoice.payments.map(p => (
                    <Box key={p._id || p} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, p: 1, background: 'rgba(16,185,129,0.05)', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{p.number || 'Pago'}</Typography>
                      <Typography variant="body2" fontWeight={700} color="success.main">{fmt.currency(p.amount)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {invoice && (
        <PaymentDialog open={payDialog} invoice={invoice}
          onClose={() => setPayDialog(false)}
          onSave={() => { setPayDialog(false); API.get(`/invoices/${id}`).then(r => setInvoice(r.data)); }} />
      )}
    </Box>
  );
}
